import { arrayMove } from "@dnd-kit/sortable";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { IconButton } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { ContentEditableEvent } from "react-contenteditable";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { selectCurrentLanguage } from "../../../app/slices/TrainingLocalizationSlice";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import PiContentEditable from "../../../components/PiContentEditable";
import PiDragButton from "../../../components/PiDragButton";
import PiEditMenu from "../../../components/PiEditMenu";
import { IAnswer } from "../../../models/Quiz";
import { IWordSpinnerQuiz } from "../../../models/interactions/WordSpinnerQuiz";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import WordQuizSentenceSegment from "../WordQuiz/WordQuizSentenceSegment";
import PiBasicQuizAddButtonSmall from "../components/PiBasicQuizAddButtonSmall";

import PiDragContainer, {
  ContainerStyle,
} from "../../../components/PiDragContainer";
import classNames from "classnames";
import useWordSpinnerQuizBody from "./WordSpinnerQuizBody.hook";

export default function WordSpinnerQuizBody() {
  const quiz = useSelector(selectSelectedElement) as IWordSpinnerQuiz;
  const { getValue } = useTrainingLocalization();

  const [x, setXPos] = useState<number>(0);
  const [y, setYPos] = useState<number>(0);

  const {
    editedSentence,
    chunks,
    onChangeAnswer,
    onDeleteAnswer,
    setSentenceIndices,
    sentenceIndices,
    registerCorrectAnswer,
    addWrongAnswer,
    onToggleCorrectAnswer,
    setIsEditingSentence,
    onChangeContentEditable,
    onBlurContentEditable,
    isEditingSentence,
    onHandleDragEnd,
    getMappedAnswers,
  } = useWordSpinnerQuizBody();

  const mappedAnswers = getMappedAnswers();

  // --- view related logic ---
  useEffect(() => {
    // calculate avg pos
    var parent = document.getElementById("wordspinnerquiz-root");
    var elem = document.getElementById(
      "wordspinnerquiz-" + sentenceIndices[sentenceIndices.length - 1]
    );

    if (elem) {
      var prect = parent!.getBoundingClientRect();
      var rect = elem!.getBoundingClientRect();

      setXPos(((rect.x - prect.x + rect.width * 0.5) / prect.width) * 100);
      setYPos(((rect.y - prect.y) / prect.height) * 100);
    }
  }, [sentenceIndices]);

  let correctChunk = chunks.find((c) => c.correct);

  const onSentenceSegmentClick = useCallback(
    (index: number) => {
      const isNeighbor = sentenceIndices.some((i) => Math.abs(i - index) <= 1);
      setSentenceIndices(isNeighbor ? [...sentenceIndices, index] : [index]);
    },
    [sentenceIndices]
  );

  //#region views
  const getSplitSentenceView = () => {
    return chunks.map((chunk, index) => {
      return (
        <div
          className={
            "flex flex-col justify-center items-center text-[64px] text-interaction-primary"
          }
          key={chunk.id}
        >
          {chunk.correct ? (
            <ArrowDropUpIcon fontSize="inherit" color="inherit" />
          ) : (
            ""
          )}
          <WordQuizSentenceSegment
            key={"wordspinnerquiz-" + index}
            id={"wordspinnerquiz-" + index}
            text={chunk.text}
            onClick={() => onSentenceSegmentClick(index)}
            highlight={sentenceIndices.includes(index)}
            isCorrect={chunk.correct}
          />
          {chunk.correct ? (
            <ArrowDropDownIcon fontSize="inherit" color="inherit" />
          ) : (
            ""
          )}
        </div>
      );
    });
  };

  const contentEditable = (
    <div style={{ display: "flex" }}>
      <PiContentEditable
        innerHtml={editedSentence}
        onChange={onChangeContentEditable}
        onBlur={onBlurContentEditable}
      />
      <IconButton
        className="text-[22px] !text-interaction-primary"
        onClick={() => {
          setIsEditingSentence(false);
        }}
      >
        <CheckIcon fontSize="inherit" color="inherit" />
      </IconButton>
    </div>
  );
  const staticDisplay = (
    <div className="flex flex-wrap justify-center items-center gap-x-[5px]">
      {chunks.length >= 1 && getSplitSentenceView()}
      {(chunks.length === 0 || chunks[0].text === "") && (
        <span
          className="text-interaction-primary-faded"
          onClick={() => setIsEditingSentence(true)}
        >
          Type a sentence
        </span>
      )}
      <IconButton
        className="text-[22px] !text-interaction-primary"
        onClick={() => setIsEditingSentence(true)}
      >
        <EditOutlinedIcon fontSize="inherit" color="inherit" />
      </IconButton>
    </div>
  );

  const contentDisplay = isEditingSentence ? contentEditable : staticDisplay;

  const addWrongAnswerButton = (
    <PiBasicQuizAddButtonSmall onClick={addWrongAnswer} />
  );

  // TODO: only show correct answer, if formatted sentence contains {0}

  const answerArray = mappedAnswers.map((answer, index) => {
    return (
      <PiDragButton
        uniqueId={answer.id}
        isDragging={false}
        key={answer.id}
        text={
          answer.correct && correctChunk
            ? correctChunk.text.replace("{", "").replace("}", "")
            : getValue(answer.text)
        }
        placeholder="Answer"
        notifyChangeSentence={() => {}}
        highlight={answer.correct}
        canDelete
        canToggle={correctChunk != null}
        isToggled={answer.correct}
        onToggle={() => onToggleCorrectAnswer(index)}
        onDelete={() => onDeleteAnswer(answer)}
        onChange={(text) => onChangeAnswer(text, answer, answer.correct)}
      />
    );
  });

  //#endregion

  return (
    <>
      <div
        id={"wordspinnerquiz-root"}
        className="h-full flex flex-col justify-start items-center gap-y-[32px]"
      >
        <div className="h-[45%] w-full bg-interaction-primary-washed rounded-[5px] pt-[32px] flex justify-center items-center">
          {contentDisplay}
        </div>
        <PiDragContainer
          onHandleDragEnd={onHandleDragEnd}
          containerElements={answerArray}
          uniqueIds={mappedAnswers.map((a) => a.id)}
          containerStyle={ContainerStyle.Grid}
        />
        {addWrongAnswerButton}
      </div>
      <div
        className={classNames(
          "absolute translate-x-[-50%] translate-y-[-8px]",
          { hidden: sentenceIndices.length === 0 }
        )}
        style={{ top: `${y}%`, left: `${x}%` }}
      >
        <PiEditMenu
          buttonSettings={[
            {
              icon: <CheckIcon fontSize="inherit" color="inherit" />,
              onClick: () => {
                registerCorrectAnswer();
              },
            },
          ]}
        />
      </div>
    </>
  );
}
